/**
 * VideoPlayer Component
 * 
 * Simplified video player for video posts featuring:
 * - ReactPlayer with working state management (based on video-experiment App.js)
 * - Basic controls: Play/Pause, Stop
 * - Seek bar with time display
 * - Volume control
 * - Speed controls: 0.5x, 0.75x, 1x, 1.5x, 2x
 * - Clean, minimal interface
 * 
 * Used in: VideoPostViewer
 */
import React, { useState, useRef, useCallback, useEffect, Suspense } from 'react';
import ReactPlayer from 'react-player';
import CaptionsWidget from './CaptionsWidget';
import {
    Box,
    IconButton,
    Typography,
    Paper,
    Stack,
    CircularProgress,
    Select,
    MenuItem,
    FormControl
} from '@mui/material';
import {
    PlayArrow,
    Pause,
    Stop,
    VolumeUp,
    VolumeOff,
    VisibilityOff,
    Visibility
} from '@mui/icons-material';

// Helper function to format time for display
const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
};

const VideoPlayer = ({ 
    videoUrl, 
    onTimeUpdate = () => {}, 
    onDurationChange = () => {},
    onPlay = () => {},
    onPause = () => {},
    isShorts = false,
    transcripts = [],
    onSeekTo = null,
    onWordDisplayed = null,
    currentTime = 0,
    onVideoVisibilityToggle = () => {}
}) => {
    const playerRef = useRef(null);

    // EXACT initialState from video-experiment App.js
    const initialState = {
        src: videoUrl,
        pip: false,
        playing: false,
        controls: false,
        light: false,
        volume: 1,
        muted: false,
        played: 0,
        loaded: 0,
        duration: 0,
        playbackRate: 1.0,
        loop: false,
        seeking: false,
        loadedSeconds: 0,
        playedSeconds: 0,
    };

    const [state, setState] = useState(initialState);
    const [videoHidden, setVideoHidden] = useState(false);

    // Update src when videoUrl changes
    useEffect(() => {
        if (videoUrl) {
            setState(prevState => ({
                ...prevState,
                src: videoUrl,
                played: 0,
                loaded: 0,
                pip: false,
            }));
        }
    }, [videoUrl]);

    // EXACT setPlayerRef from video-experiment App.js with volume sync
    const setPlayerRef = useCallback((player) => {
        if (!player) return;
        playerRef.current = player;
        
        // Listen to volume changes from keyboard/other controls
        const handleVolumeChange = () => {
            if (player.volume !== undefined) {
                setState(prevState => ({
                    ...prevState,
                    volume: player.volume,
                    muted: player.muted || false
                }));
            }
        };
        
        // Add event listener for volume changes
        if (player.addEventListener) {
            player.addEventListener('volumechange', handleVolumeChange);
        }
    }, []);

    // EXACT handlers from video-experiment App.js with debouncing
    const [playPauseDebounce, setPlayPauseDebounce] = useState(null);
    
    const handlePlayPause = useCallback(() => {
        // Clear any existing debounce
        if (playPauseDebounce) {
            clearTimeout(playPauseDebounce);
        }
        
        // Set new debounced action
        const newTimeout = setTimeout(() => {
            setState(prevState => ({ ...prevState, playing: !prevState.playing }));
            setPlayPauseDebounce(null);
        }, 100);
        
        setPlayPauseDebounce(newTimeout);
    }, [playPauseDebounce]);

    const handleStop = useCallback(() => {
        setState(prevState => ({ ...prevState, src: undefined, playing: false }));
    }, []);

    const handleVolumeChange = useCallback((event) => {
        const newVolume = parseFloat(event.target.value);
        setState(prevState => ({ 
            ...prevState, 
            volume: newVolume,
            muted: newVolume === 0
        }));
        
        // Also update the actual player volume
        if (playerRef.current) {
            playerRef.current.volume = newVolume;
            playerRef.current.muted = newVolume === 0;
        }
    }, []);

    const handleToggleMuted = useCallback(() => {
        setState(prevState => ({ 
            ...prevState, 
            muted: !prevState.muted,
            // If unmuting, restore previous volume
            volume: !prevState.muted ? prevState.volume : (prevState.volume === 0 ? 0.5 : prevState.volume)
        }));
    }, []);

    const handleSeekMouseDown = () => {
        setState(prevState => ({ ...prevState, seeking: true }));
    };

    const handleSeekChange = (event, newValue) => {
        setState(prevState => ({ ...prevState, played: newValue / 100 }));
    };

    const handleSeekMouseUp = (event, newValue) => {
        setState(prevState => ({ ...prevState, seeking: false }));
        if (playerRef.current) {
            playerRef.current.currentTime = Number.parseFloat(newValue / 100) * playerRef.current.duration;
        }
    };

    const handleSetPlaybackRate = useCallback((rate) => {
        setState(prevState => ({ ...prevState, playbackRate: parseFloat(rate) }));
    }, []);

    const handleToggleVideoVisibility = useCallback(() => {
        setVideoHidden(prev => {
            const newHidden = !prev;
            onVideoVisibilityToggle(newHidden);
            return newHidden;
        });
    }, [onVideoVisibilityToggle]);

    // EXACT event handlers from video-experiment App.js
    const handlePlay = useCallback(() => {
        setState(prevState => ({ ...prevState, playing: true, src: videoUrl }));
        onPlay();
    }, [videoUrl, onPlay]);

    const handlePause = useCallback(() => {
        setState(prevState => ({ ...prevState, playing: false }));
        onPause();
    }, [onPause]);

    const handleProgress = (state) => {
        const player = playerRef.current;
        // We only want to update time slider if we are not currently seeking
        if (!player || state.seeking || !player.buffered?.length) return;

        setState(prevState => ({
            ...prevState,
            loadedSeconds: player.buffered?.end(player.buffered?.length - 1),
            loaded: player.buffered?.end(player.buffered?.length - 1) / player.duration,
        }));
    };

    const handleTimeUpdate = () => {
        const player = playerRef.current;

        // We only want to update time slider if we are not currently seeking
        if (!player || state.seeking) return;

        if (!player.duration) return;

        setState(prevState => ({
            ...prevState,
            playedSeconds: player.currentTime,
            played: player.currentTime / player.duration,
        }));
        
        onTimeUpdate(player.currentTime);
    };

    const handleEnded = useCallback(() => {
        setState(prevState => ({ ...prevState, playing: prevState.loop }));
    }, []);

    const handleDurationChange = () => {
        const player = playerRef.current;
        if (!player) return;

        const duration = player.duration;
        setState(prevState => ({ ...prevState, duration: duration }));
        onDurationChange(duration);
    };

    const speedOptions = [0.5, 0.75, 1, 1.5, 2];

    const {
        src,
        playing,
        controls,
        light,
        volume,
        muted,
        loop,
        played,
        // loaded,
        duration,
        playbackRate,
        pip,
        playedSeconds,
    } = state;

    // Use the passed isShorts prop instead of detecting from URL
    // const isShorts = typeof videoUrl === 'string' && videoUrl.includes('/shorts/');

    if (!videoUrl) {
        return (
            <Paper sx={{ p: 3, textAlign: 'center', backgroundColor: '#f5f5f5' }}>
                <Typography variant="h6" color="text.secondary">
                    No video available
                </Typography>
            </Paper>
        );
    }

    return (
        <Paper sx={{ overflow: 'hidden' }}>
            {/* Video Player - Shrinks to control panel height when hidden */}
            <Box sx={{ 
                position: 'relative', 
                backgroundColor: '#000',
                ...(isShorts && {
                    // For shorts, responsive container size
                    maxHeight: { xs: 'none', lg: '70vh' },
                    width: { xs: '100%', lg: 'fit-content' },
                    maxWidth: '100%',
                    mx: 'auto', // Center horizontally
                    display: 'flex',
                    justifyContent: 'center'
                }),
                ...(videoHidden && {
                    // When video is hidden, shrink to control panel height
                    height: 'auto',
                    minHeight: 'auto',
                    backgroundColor: 'background.paper'
                })
            }}>
                <Suspense 
                    fallback={
                        <Box sx={{ 
                            display: 'flex', 
                            justifyContent: 'center', 
                            alignItems: 'center', 
                            height: isShorts ? { xs: '56.25vw', lg: '70vh' } : '400px',
                            width: isShorts ? { xs: '100%', lg: 'calc(70vh * 9/16)' } : '100%',
                            aspectRatio: isShorts ? '9/16' : '16/9' 
                        }}>
                            <CircularProgress color="primary" />
                        </Box>
                    }
                >
                    <ReactPlayer
                        ref={setPlayerRef}
                        className="react-player"
                        style={{ 
                            ...(isShorts ? {
                                // For shorts: maintain aspect ratio, max height constraint handled by container
                                width: '100%',
                                height: 'auto',
                                maxWidth: '100%',
                                aspectRatio: '9/16'
                            } : {
                                // For regular videos: maintain full width
                                width: '100%', 
                                height: 'auto', 
                                aspectRatio: '16/9'
                            }),
                            // Hide video visually when videoHidden is true, but keep it playing
                            ...(videoHidden && {
                                position: 'absolute',
                                top: '-9999px',
                                left: '-9999px',
                                width: '1px',
                                height: '1px',
                                opacity: 0,
                                pointerEvents: 'none'
                            })
                        }}
                        src={src}
                        pip={pip}
                        playing={playing}
                        controls={controls}
                        light={light}
                        loop={loop}
                        playbackRate={playbackRate}
                        volume={volume}
                        muted={muted}
                        config={{
                            youtube: {
                                color: 'white'
                            },
                            vimeo: {
                                color: 'ffffff'
                            }
                        }}
                        // onLoadStart={() => console.log('onLoadStart')}
                        // onReady={() => console.log('onReady')}
                        // onStart={(e) => console.log('onStart', e)}
                        onPlay={handlePlay}
                        onPause={handlePause}
                        // onRateChange={(rate) => console.log('onRateChange', rate)}
                        // onSeeking={(e) => console.log('onSeeking', e)}
                        // onSeeked={(e) => console.log('onSeeked', e)}
                        onEnded={handleEnded}
                        onError={(e) => console.log('onError', e)}
                        onTimeUpdate={handleTimeUpdate}
                        onProgress={handleProgress}
                        onDurationChange={handleDurationChange}
                    />
                </Suspense>

                {/* Video Hidden Message - Show when video is hidden */}
                {videoHidden && (
                    <Box sx={{
                        width: '100%',
                        height: '100px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: '1px solid',
                        borderColor: 'divider',
                        borderRadius: 1
                    }}>
                        <Typography variant="body1" sx={{ 
                            color: 'text.secondary',
                            textAlign: 'center'
                        }}>
                            Audio Playing • Video Hidden
                        </Typography>
                    </Box>
                )}
            </Box>

            {/* Custom Controls */}
            <Box sx={{ p: 2 }}>
                {/* Desktop Layout */}
                <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                    {/* Desktop Row 1: Play/Stop, Current time, Seek bar, Total time */}
                    <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <IconButton 
                                onClick={handlePlayPause} 
                                color="primary"
                                size="large"
                            >
                                {playing ? <Pause /> : <PlayArrow />}
                            </IconButton>
                            <IconButton onClick={handleStop} color="primary">
                                <Stop />
                            </IconButton>
                            <IconButton 
                                onClick={handleToggleVideoVisibility} 
                                color="primary"
                                title={videoHidden ? "Show Video" : "Hide Video"}
                            >
                                {videoHidden ? <Visibility /> : <VisibilityOff />}
                            </IconButton>
                        </Box>

                        <Typography variant="body2" sx={{ minWidth: '50px' }}>
                            {formatTime(playedSeconds)}
                        </Typography>

                        <Box sx={{ flex: 1, mx: 2 }}>
                            <input
                                type="range"
                                min={0}
                                max={0.999999}
                                step="any"
                                value={played}
                                onMouseDown={handleSeekMouseDown}
                                onChange={(e) => handleSeekChange(e, e.target.value * 100)}
                                onMouseUp={(e) => handleSeekMouseUp(e, e.target.value * 100)}
                                onTouchStart={handleSeekMouseDown}
                                onTouchEnd={(e) => handleSeekMouseUp(e, e.target.value * 100)}
                                style={{ width: '100%' }}
                            />
                        </Box>

                        <Typography variant="body2" sx={{ minWidth: '50px' }}>
                            {formatTime(duration)}
                        </Typography>
                    </Stack>

                    {/* Desktop Row 2: Volume (left) and Speed (right) */}
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                        {/* Volume Control - Left aligned */}
                        <Box sx={{ display: 'flex', alignItems: 'center', minWidth: '200px' }}>
                            <IconButton onClick={handleToggleMuted} color="primary" size="small">
                                {muted || volume === 0 ? <VolumeOff /> : <VolumeUp />}
                            </IconButton>
                            <Typography variant="caption" sx={{ mx: 1 }}>
                                Volume:
                            </Typography>
                            <input
                                type="range"
                                min={0}
                                max={1}
                                step="any"
                                value={volume}
                                onChange={handleVolumeChange}
                                style={{ width: '100px' }}
                            />
                            <Typography variant="caption" sx={{ ml: 1, minWidth: '30px' }}>
                                {Math.round(volume * 100)}%
                            </Typography>
                        </Box>

                        {/* Speed Controls - Right aligned */}
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography variant="caption" sx={{ mr: 1 }}>
                                Speed:
                            </Typography>
                            {isShorts ? (
                                <FormControl size="small" sx={{ minWidth: 60 }}>
                                    <Select
                                        value={playbackRate}
                                        onChange={(e) => handleSetPlaybackRate(e.target.value)}
                                        sx={{ fontSize: '0.75rem', height: '28px' }}
                                    >
                                        {speedOptions.map((speed) => (
                                            <MenuItem key={speed} value={speed} sx={{ fontSize: '0.75rem' }}>
                                                {speed}x
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            ) : (
                                speedOptions.map((speed) => (
                                    <IconButton
                                        key={speed}
                                        onClick={() => handleSetPlaybackRate(speed)}
                                        size="small"
                                        sx={{
                                            backgroundColor: playbackRate === speed ? 'primary.main' : 'transparent',
                                            color: playbackRate === speed ? 'white' : 'text.primary',
                                            fontSize: '0.75rem',
                                            minWidth: '35px',
                                            height: '28px',
                                            mx: 0.25,
                                            '&:hover': {
                                                backgroundColor: playbackRate === speed ? 'primary.dark' : 'action.hover',
                                            }
                                        }}
                                    >
                                        {speed}x
                                    </IconButton>
                                ))
                            )}
                        </Box>
                    </Stack>
                </Box>

                {/* Mobile Layout */}
                                    <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                        {/* Mobile Row 1: Seek progress bar only */}
                        {/* FIXED: Added onTouchStart for mobile seek functionality - prevents seek issues on touch devices */}
                        <Box sx={{ mb: 2 }}>
                            <input
                                type="range"
                                min={0}
                                max={0.999999}
                                step="any"
                                value={played}
                                onMouseDown={handleSeekMouseDown}
                                onChange={(e) => handleSeekChange(e, e.target.value * 100)}
                                onMouseUp={(e) => handleSeekMouseUp(e, e.target.value * 100)}
                                onTouchStart={handleSeekMouseDown}
                                onTouchEnd={(e) => handleSeekMouseUp(e, e.target.value * 100)}
                                style={{ width: '100%' }}
                            />
                        </Box>

                    {/* Mobile Row 2: Play/Stop and Time */}
                    <Stack direction="row" spacing={2} alignItems="center" justifyContent="center" sx={{ mb: 2 }}>
                        <IconButton 
                            onClick={handlePlayPause} 
                            color="primary"
                            size="large"
                        >
                            {playing ? <Pause /> : <PlayArrow />}
                        </IconButton>
                        <IconButton onClick={handleStop} color="primary">
                            <Stop />
                        </IconButton>
                        <IconButton 
                            onClick={handleToggleVideoVisibility} 
                            color="primary"
                            title={videoHidden ? "Show Video" : "Hide Video"}
                        >
                            {videoHidden ? <Visibility /> : <VisibilityOff />}
                        </IconButton>
                        <Typography variant="body2" sx={{ ml: 2 }}>
                            {formatTime(playedSeconds)} / {formatTime(duration)}
                        </Typography>
                    </Stack>

                    {/* Mobile Row 3: Volume */}
                    <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        mb: 2 
                    }}>
                        <IconButton onClick={handleToggleMuted} color="primary" size="small">
                            {muted || volume === 0 ? <VolumeOff /> : <VolumeUp />}
                        </IconButton>
                        <Typography variant="caption" sx={{ mx: 1 }}>
                            Volume:
                        </Typography>
                        <input
                            type="range"
                            min={0}
                            max={1}
                            step="any"
                            value={volume}
                            onChange={handleVolumeChange}
                            style={{ width: '120px' }}
                        />
                        <Typography variant="caption" sx={{ ml: 1, minWidth: '30px' }}>
                            {Math.round(volume * 100)}%
                        </Typography>
                    </Box>

                    {/* Mobile Row 4: Speed */}
                    <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}>
                        <Typography variant="caption" sx={{ mr: 1 }}>
                            Speed:
                        </Typography>
                        {isShorts ? (
                            <FormControl size="small" sx={{ minWidth: 60 }}>
                                <Select
                                    value={playbackRate}
                                    onChange={(e) => handleSetPlaybackRate(e.target.value)}
                                    sx={{ fontSize: '0.75rem', height: '28px' }}
                                >
                                    {speedOptions.map((speed) => (
                                        <MenuItem key={speed} value={speed} sx={{ fontSize: '0.75rem' }}>
                                            {speed}x
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        ) : (
                            speedOptions.map((speed) => (
                                <IconButton
                                    key={speed}
                                    onClick={() => handleSetPlaybackRate(speed)}
                                    size="small"
                                    sx={{
                                        backgroundColor: playbackRate === speed ? 'primary.main' : 'transparent',
                                        color: playbackRate === speed ? 'white' : 'text.primary',
                                        fontSize: '0.75rem',
                                        minWidth: '35px',
                                        height: '28px',
                                        mx: 0.25,
                                        '&:hover': {
                                            backgroundColor: playbackRate === speed ? 'primary.dark' : 'action.hover',
                                        }
                                    }}
                                >
                                    {speed}x
                                </IconButton>
                            ))
                        )}
                    </Box>
                </Box>
            </Box>
        </Paper>
    );
};

export default VideoPlayer; 